import React, { useState, SyntheticEvent } from "react";
import {
  Box,
  Typography,
  Avatar,
  Badge,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import { blue, red, green, yellow } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MailIcon from "@mui/icons-material/Mail";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DisabledByDefaultOutlinedIcon from "@mui/icons-material/DisabledByDefaultOutlined";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";

// Types and Interfaces
interface NotificationItem {
  id: number;
  type: "all" | "focused" | "other";
  title: string;
  description: string;
  time: string;
  icon: React.ReactElement;
  avatarColor: string;
  badge?: string;
  badgeColor?: string;
  badgeTextColor?: string;
  actions: string[];
}

interface CustomTheme {
  palette: {
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
    greyscale?: {
      divider_1?: string;
    };
    system?: {
      error?: string;
    };
  };
}

const notifications: NotificationItem[] = [
  {
    id: 1,
    type: "all",
    title: "GRN Approval Pending",
    description: "GRN #12345 requires your approval. Click to review.",
    time: "2 min ago",
    icon: <AssignmentTurnedInIcon sx={{ color: yellow[700] }} />,
    avatarColor: yellow[100],
    badge: "New",
    badgeColor: blue[100],
    badgeTextColor: blue[700],
    actions: ["Approve", "Reject", "View"],
  },
  {
    id: 2,
    type: "all",
    title: "Return Approved",
    description: "Return #12345 – Your return has been approved.",
    time: "2 min ago",
    icon: <CheckCircleIcon sx={{ color: green[500] }} />,
    avatarColor: green[100],
    actions: ["View"],
  },
  {
    id: 3,
    type: "all",
    title: "Last hours of -50% Premium",
    description:
      "Say goodbye to design hassles and hello to effortless creativity with Freepik Premium. Get projects done quick and slick, no sweat, with ac...",
    time: "2 min ago",
    icon: <MailIcon sx={{ color: blue[500] }} />,
    avatarColor: blue[100],
    actions: ["View"],
  },
  {
    id: 4,
    type: "focused",
    title: "GRN Approval Pending",
    description: "GRN #12345 requires your approval. Click to review.",
    time: "2 min ago",
    icon: <AssignmentTurnedInIcon sx={{ color: yellow[700] }} />,
    avatarColor: yellow[100],
    badge: "New",
    badgeColor: blue[100],
    badgeTextColor: blue[700],
    actions: ["Approve", "Reject", "View"],
  },
  {
    id: 5,
    type: "other",
    title: "Last hours of -50% Premium",
    description:
      "Say goodbye to design hassles and hello to effortless creativity with Freepik Premium. Get projects done quick and slick, no sweat, with ac...",
    time: "2 min ago",
    icon: <MailIcon sx={{ color: blue[500] }} />,
    avatarColor: blue[100],
    actions: ["View"],
  },
  {
    id: 6,
    type: "all",
    title: "GRN Approval Pending",
    description: "GRN #12345 requires your approval. Click to review.",
    time: "2 min ago",
    icon: <AssignmentTurnedInIcon sx={{ color: yellow[700] }} />,
    avatarColor: yellow[100],
    badge: "New",
    badgeColor: blue[100],
    badgeTextColor: blue[700],
    actions: ["Approve", "Reject", "View"],
  },
];

const Notification: React.FC = () => {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const handleTabChange = (event: SyntheticEvent, newValue: number): void => {
    setTabIndex(newValue);
  };

  const filteredNotifications = notifications.filter((notification: NotificationItem) => {
    if (tabIndex === 0) return notification.type === "all";
    if (tabIndex === 1) return notification.type === "focused";
    if (tabIndex === 2) return notification.type === "other";
    return false;
  });

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        boxShadow: 3,
        borderRadius: 2,
        width: 420,
        height: "80vh",
        mx: "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: theme.palette.greyscale?.divider_1 || '#E0E0E0',
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
          Notifications
        </Typography>
        <IconButton>
          <SettingsOutlinedIcon />
        </IconButton>
      </Box>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: theme.palette.greyscale?.divider_1 || '#E0E0E0',
        }}
      >
        <Tab
          label={
            <Badge badgeContent={6} color="secondary">
              All
            </Badge>
          }
        />
        <Tab
          label={
            <Badge badgeContent={4} color="primary">
              Focused
            </Badge>
          }
        />
        <Tab
          label={
            <Badge badgeContent={2} color="primary">
              Other
            </Badge>
          }
        />
      </Tabs>
      <Box
        sx={{
          px: 0,
          py: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          overflowY: "auto",
          flexGrow: 1,
          height: "calc(100vh - 200px)",
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            width: "100%",
            px: 1,
          }}
        >
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            Today
          </Typography>
        </Box>

        <Box px={2}>
          {filteredNotifications.map((notification: NotificationItem) => (
            <React.Fragment key={notification.id}>
              <Box
                sx={{
                  display: "flex",
                  paddingLeft: 0,
                  width: "100%",
                  my: 1,
                  gap: 2,
                }}
              >
                <Box py={1}>
                  <Avatar sx={{ bgcolor: notification.avatarColor }}>
                    {notification.icon}
                  </Avatar>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      <strong>{notification.title}</strong>
                      {notification.badge && (
                        <Box
                          component="span"
                          sx={{
                            bgcolor: notification.badgeColor,
                            color: notification.badgeTextColor,
                            borderRadius: "12px",
                            px: 1,
                            ml: 1,
                            fontSize: '0.75rem',
                            display: 'inline-block'
                          }}
                        >
                          {notification.badge}
                        </Box>
                      )}
                    </Typography>
                    <IconButton
                      edge="end"
                      sx={{
                        height: 24,
                        width: 24,
                        borderRadius: 1,
                      }}
                    >
                      <DisabledByDefaultOutlinedIcon
                        sx={{ color: theme.palette.system?.error || '#f44336' }}
                      />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    {notification.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    sx={{ fontSize: '0.625rem' }}
                  >
                    {notification.time}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {notification.actions
                        .filter(
                          (action: string) =>
                            action === "Approve" || action === "Reject"
                        )
                        .map((action: string, index: number) => (
                          <Button
                            key={index}
                            variant={
                              action === "Approve" ? "contained" : "outlined"
                            }
                            color="primary"
                            size="small"
                            sx={styles.btn_secondary}
                          >
                            {action}
                          </Button>
                        ))}
                    </Box>
                    {notification.actions.includes("View") && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={styles.btn_secondary}
                      >
                        View
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
              <Divider />
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Button variant="text" sx={styles.btn_text}>
          View All
        </Button>
      </Box>
    </Box>
  );
};

export default Notification;